import React, { useState, useContext } from 'react';
import { UserContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Building2, 
  Upload, 
  FileText, 
  Image, 
  Phone, 
  User, 
  X,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const CompanySetup = ({ onCompanyCreated }) => {
  const { user, API } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    raison_sociale: '',
    numero_ncc: '',
    numero_rccm: '',
    telephone_proprietaire: '',
    nom_proprietaire: '',
    prenom_proprietaire: ''
  });
  const [dfeDocument, setDfeDocument] = useState(null);
  const [rccmDocument, setRccmDocument] = useState(null);
  const [logo, setLogo] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (type, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileData = {
          data: e.target.result,
          name: file.name,
          size: file.size,
          type: file.type
        };
        
        switch (type) {
          case 'dfe':
            setDfeDocument(fileData);
            toast.success('DFE téléchargée avec succès');
            break;
          case 'rccm':
            setRccmDocument(fileData);
            toast.success('RCCM téléchargé avec succès');
            break;
          case 'logo':
            if (file.type.startsWith('image/')) {
              setLogo(fileData);
              toast.success('Logo téléchargé avec succès');
            } else {
              toast.error('Veuillez sélectionner une image pour le logo');
            }
            break;
        }
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const removeFile = (type) => {
    switch (type) {
      case 'dfe':
        setDfeDocument(null);
        break;
      case 'rccm':
        setRccmDocument(null);
        break;
      case 'logo':
        setLogo(null);
        break;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validation
      if (!formData.raison_sociale.trim() || !formData.numero_ncc.trim() || 
          !formData.numero_rccm.trim() || !formData.telephone_proprietaire.trim() ||
          !formData.nom_proprietaire.trim() || !formData.prenom_proprietaire.trim()) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }

      setLoading(true);

      const companyPayload = {
        ...formData,
        owner_user_id: user.id,
        dfe_document: dfeDocument ? dfeDocument.data : null,
        rccm_document: rccmDocument ? rccmDocument.data : null,
        logo: logo ? logo.data : null
      };

      await axios.post(`${API}/company`, companyPayload);

      toast.success('Entreprise créée avec succès !');
      toast.info('Redirection vers l\'application...');
      
      // Attendre un peu puis notifier le parent
      setTimeout(() => {
        onCompanyCreated();
      }, 2000);

    } catch (error) {
      console.error('Error creating company:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'entreprise');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-4">
      <div className="w-full max-w-4xl space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Création de votre Entreprise
          </h1>
          <p className="text-gray-600 text-lg mb-6">
            Complétez les informations de votre boulangerie pour continuer
          </p>
        </div>

        <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              Informations de l'Entreprise
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Informations Entreprise */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Informations Entreprise
                  </h3>
                  
                  {/* Raison Sociale */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Raison Sociale (Nom de l'entreprise) *
                    </Label>
                    <Input
                      value={formData.raison_sociale}
                      onChange={(e) => handleInputChange('raison_sociale', e.target.value)}
                      className="h-12 text-base"
                      placeholder="Ex: Boulangerie Artisanale Durand"
                      required
                    />
                  </div>

                  {/* Numéro NCC + DFE */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Numéro NCC *
                    </Label>
                    <Input
                      value={formData.numero_ncc}
                      onChange={(e) => handleInputChange('numero_ncc', e.target.value)}
                      className="h-12 text-base"
                      placeholder="Ex: 1234567890123"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      DFE (Déclaration Fiscale d'Existence)
                    </Label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileUpload('dfe', e)}
                      className="hidden"
                      id="dfe-upload"
                    />
                    <Button
                      type="button"
                      onClick={() => document.getElementById('dfe-upload').click()}
                      variant="outline"
                      className="w-full h-12 border-2 border-dashed border-gray-300 hover:border-indigo-400"
                    >
                      <Upload className="h-5 w-5 mr-2" />
                      Télécharger la DFE
                    </Button>
                    
                    {dfeDocument && (
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-green-800">
                            {dfeDocument.name} ({formatFileSize(dfeDocument.size)})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile('dfe')}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Numéro RCCM + Document */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Numéro RCCM *
                    </Label>
                    <Input
                      value={formData.numero_rccm}
                      onChange={(e) => handleInputChange('numero_rccm', e.target.value)}
                      className="h-12 text-base"
                      placeholder="Ex: CI-ABJ-2024-B-12345"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Document RCCM
                    </Label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileUpload('rccm', e)}
                      className="hidden"
                      id="rccm-upload"
                    />
                    <Button
                      type="button"
                      onClick={() => document.getElementById('rccm-upload').click()}
                      variant="outline"
                      className="w-full h-12 border-2 border-dashed border-gray-300 hover:border-indigo-400"
                    >
                      <Upload className="h-5 w-5 mr-2" />
                      Télécharger le RCCM
                    </Button>
                    
                    {rccmDocument && (
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-green-800">
                            {rccmDocument.name} ({formatFileSize(rccmDocument.size)})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile('rccm')}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Informations Propriétaire + Logo */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Informations Propriétaire
                  </h3>

                  {/* Logo */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Logo de l'entreprise
                    </Label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload('logo', e)}
                      className="hidden"
                      id="logo-upload"
                    />
                    <Button
                      type="button"
                      onClick={() => document.getElementById('logo-upload').click()}
                      variant="outline"
                      className="w-full h-12 border-2 border-dashed border-gray-300 hover:border-indigo-400"
                    >
                      <Image className="h-5 w-5 mr-2" />
                      Télécharger le Logo
                    </Button>
                    
                    {logo && (
                      <div className="relative">
                        <img
                          src={logo.data}
                          alt="Logo entreprise"
                          className="w-32 h-32 object-contain mx-auto rounded-lg border border-gray-200 bg-white p-2"
                        />
                        <button
                          type="button"
                          onClick={() => removeFile('logo')}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Nom Propriétaire */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Nom du Propriétaire *
                    </Label>
                    <Input
                      value={formData.nom_proprietaire}
                      onChange={(e) => handleInputChange('nom_proprietaire', e.target.value)}
                      className="h-12 text-base"
                      placeholder="Ex: KOUAME"
                      required
                    />
                  </div>

                  {/* Prénom Propriétaire */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Prénom du Propriétaire *
                    </Label>
                    <Input
                      value={formData.prenom_proprietaire}
                      onChange={(e) => handleInputChange('prenom_proprietaire', e.target.value)}
                      className="h-12 text-base"
                      placeholder="Ex: Jean-Baptiste"
                      required
                    />
                  </div>

                  {/* Téléphone */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Numéro de Téléphone *
                    </Label>
                    <Input
                      value={formData.telephone_proprietaire}
                      onChange={(e) => handleInputChange('telephone_proprietaire', e.target.value)}
                      className="h-12 text-base"
                      placeholder="Ex: +225 07 12 34 56 78"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Résumé avant validation */}
              <div className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
                <h4 className="font-medium text-indigo-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Résumé de votre Entreprise
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p><strong>Raison sociale:</strong> {formData.raison_sociale || 'Non renseigné'}</p>
                    <p><strong>NCC:</strong> {formData.numero_ncc || 'Non renseigné'}</p>
                    <p><strong>RCCM:</strong> {formData.numero_rccm || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <p><strong>Propriétaire:</strong> {formData.prenom_proprietaire} {formData.nom_proprietaire}</p>
                    <p><strong>Téléphone:</strong> {formData.telephone_proprietaire || 'Non renseigné'}</p>
                    <p><strong>Documents:</strong> 
                      {dfeDocument ? ' DFE✓' : ' DFE✗'} 
                      {rccmDocument ? ' RCCM✓' : ' RCCM✗'}
                      {logo ? ' Logo✓' : ' Logo✗'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="text-center">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 text-base bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-3"></div>
                      Création de l'entreprise...
                    </>
                  ) : (
                    <>
                      <Building2 className="h-5 w-5 mr-3" />
                      Créer mon Entreprise et Accéder à l'Application
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="bg-white/60 backdrop-blur-sm border-0">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800 mb-2">Documents Requis</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• <strong>DFE</strong>: Déclaration Fiscale d'Existence (PDF ou image)</li>
                  <li>• <strong>RCCM</strong>: Registre du Commerce et du Crédit Mobilier (PDF ou image)</li>
                  <li>• <strong>Logo</strong>: Image de votre entreprise (optionnel mais recommandé)</li>
                  <li>• Tous ces documents seront sécurisés et accessibles uniquement par vous</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CompanySetup;